import React, { type FC, useCallback, useState } from 'react';

import { Xmark } from '@gravity-ui/icons';
import { Button, Icon, Loader } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { formatBytes } from 'client/lib/utils';
import { IQuickApp, QuickAppStatus } from 'client/model/privateApps';
import { Channel } from 'client/model/skill';
import * as keyset from 'i18n/PrivateApps';

import { QuickAppSmartScreenLabel, QuickAppTvLabel } from '../labels/QuickAppsLabels';
import { QuickAppsModal } from '../QuickAppsModal/QuickAppsModal';

import styles from './QuickAppsList.module.scss';

interface QuickAppListElementProps {
    quickApp: Partial<IQuickApp>;
    setDeleteAppDraft: (deleteDraft: IQuickApp) => void;
}

interface QuickAppsListProps {
    quickApps: Partial<IQuickApp>[];
    onDelete: (deletedId: string, channel: Channel) => Promise<void>;
}

const t = i18n(keyset);

const isQuickApp = (maybeQuickApp: Partial<IQuickApp>): maybeQuickApp is IQuickApp =>
    !!maybeQuickApp && !!maybeQuickApp.id && !!maybeQuickApp.name && !!maybeQuickApp.channel;

const QuickAppListElement: FC<QuickAppListElementProps> = ({ quickApp, setDeleteAppDraft }) => {
    if (!quickApp.id) {
        return (
            <li className={clx(styles.quickApp, styles.quickApp_loadingDummy)}>
                <Loader size="s" />
            </li>
        );
    }

    let label: React.ReactNode = quickApp.size_b ? formatBytes(quickApp.size_b) : null;
    if (quickApp.status === QuickAppStatus.ERROR) {
        label = <span className={styles.quickApp__labelError}>{t('error')}</span>;
    }
    if (quickApp.status === QuickAppStatus.PROCESSING) {
        label = <span className={styles.quickApp__labelLoading}>{t('check')}</span>;
    }

    const onClick = () => {
        if (!isQuickApp(quickApp)) return;
        setDeleteAppDraft(quickApp);
    };

    return (
        <li className={styles.quickApp}>
            <Button
                className={styles.quickApp__deleteButton}
                onClick={onClick}
                pin="circle-circle"
                size="s"
                view="raised"
            >
                <Icon data={Xmark} />
            </Button>
            <div
                className={styles.quickApp__icon}
                style={quickApp.logo_url ? { backgroundImage: `url(${quickApp.logo_url})` } : {}}
            />
            <div className={styles.quickApp__name}>{quickApp.name}</div>
            <div>{label}</div>
        </li>
    );
};

export const QuickAppsList: FC<QuickAppsListProps> = ({ quickApps, onDelete }) => {
    const [deleteAppDraft, setDeleteAppDraft] = useState<IQuickApp | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const tvApps = quickApps.filter((app) => app.channel === Channel.TvApp);
    const smartScreenApp = quickApps.filter((app) => app.channel === Channel.SmartScreenApp);

    const onDeleteConfirm = useCallback(async () => {
        if (!deleteAppDraft || !deleteAppDraft.id) return;
        setIsLoading(true);
        await onDelete(deleteAppDraft.id, deleteAppDraft.channel);
        setDeleteAppDraft(null);
        setIsLoading(false);
    }, [onDelete, deleteAppDraft]);

    return (
        <>
            {tvApps.length ? (
                <>
                    <QuickAppTvLabel classNameExternal={styles.quickAppGroupLabel} />
                    <ul className={styles.quickAppsList}>
                        {tvApps.map((app) => (
                            <QuickAppListElement
                                key={app.id || app.name}
                                quickApp={app}
                                setDeleteAppDraft={setDeleteAppDraft}
                            />
                        ))}
                    </ul>
                </>
            ) : null}
            {smartScreenApp.length ? (
                <>
                    <QuickAppSmartScreenLabel classNameExternal={styles.quickAppGroupLabel} />
                    <ul className={styles.quickAppsList}>
                        {smartScreenApp.map((app) => (
                            <QuickAppListElement
                                key={app.id || app.name}
                                quickApp={app}
                                setDeleteAppDraft={setDeleteAppDraft}
                            />
                        ))}
                    </ul>
                </>
            ) : null}
            <QuickAppsModal
                open={!!deleteAppDraft}
                onClose={() => setDeleteAppDraft(null)}
                onAccept={onDeleteConfirm}
                title={t('delete-file-question')}
                text={t('delete-file-notice')}
                loading={isLoading}
            />
        </>
    );
};
